import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { attachScrollTriggerOrPlayNow } from "../utils/scrollTriggerHelpers";

gsap.registerPlugin(ScrollTrigger);

/**
 * Experience block animation.
 *
 * Trigger: ScrollTrigger one-shot all'entrata in viewport (top 85%).
 *
 * Sequenza temporale:
 *   T=0.0  Titolo (fade + slide-up + blur)
 *   T=0.2  Logo inline (fade + scale)
 *   T=0.3  Testo (fade + slide-up + blur)
 *   T=0.5  CTA (fade + slide-up + blur)
 *   T=0.6  Immagini gallery in stagger 0.15s (fade + scale + blur)
 *
 * Durata totale ~2s + stagger immagini.
 */

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const initExperienceBlock = (block) => {
  const title = block.querySelector("[data-experience-title]");
  const logo = block.querySelector("[data-experience-logo]");
  const text = block.querySelector("[data-experience-text]");
  const cta = block.querySelector("[data-experience-cta]");
  const galleryItems = block.querySelectorAll("[data-experience-gallery-item]");

  if (!title) return;

  // Reduced motion: niente animazione, mostro subito tutto
  if (prefersReducedMotion()) {
    block.setAttribute("data-experience-ready", "true");
    return;
  }

  // Stato iniziale GSAP
  gsap.set(title, { opacity: 0, y: 24, filter: "blur(8px)" });
  if (logo) gsap.set(logo, { opacity: 0, scale: 0.8 });
  if (text) gsap.set(text, { opacity: 0, y: 24, filter: "blur(8px)" });
  if (cta) gsap.set(cta, { opacity: 0, y: 16, filter: "blur(6px)" });
  if (galleryItems.length) {
    gsap.set(galleryItems, { opacity: 0, scale: 1.1, filter: "blur(12px)" });
  }

  // Timeline orchestrata one-shot (paused, attiviamo via helper)
  const tl = gsap.timeline({ paused: true });

  // T=0.0 - Titolo
  tl.to(title, {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    duration: 1.2,
    ease: "power2.out",
  }, 0);

  // T=0.2 - Logo inline (fade + scale, leggero "respiro")
  if (logo) {
    tl.to(logo, {
      opacity: 1,
      scale: 1,
      duration: 1.0,
      ease: "power2.out",
    }, 0.2);
  }

  // T=0.3 - Testo
  if (text) {
    tl.to(text, {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      duration: 1.0,
      ease: "power2.out",
    }, 0.3);
  }

  // T=0.5 - CTA
  if (cta) {
    tl.to(cta, {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      duration: 0.8,
      ease: "power2.out",
    }, 0.5);
  }

  // T=0.6 - Immagini gallery in stagger
  if (galleryItems.length) {
    tl.to(galleryItems, {
      opacity: 1,
      scale: 1,
      filter: "blur(0px)",
      duration: 1.2,
      ease: "power2.out",
      stagger: 0.15,
    }, 0.6);
  }

  // Attach ScrollTrigger o play immediato se già in viewport
  attachScrollTriggerOrPlayNow(tl, {
    trigger: block,
    onComplete: () => block.setAttribute("data-experience-ready", "true"),
  });
};

const initAllExperience = () => {
  const blocks = document.querySelectorAll("[data-experience-block]");
  blocks.forEach(initExperienceBlock);
};

export default () => {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAllExperience);
  } else {
    initAllExperience();
  }
};